import { fetchServiceEndpoints } from 'actions/serviceEndpoints';
import {
  fetchAddressTypes,
  fetchJobTitles,
  fetchPersonTitles,
  getCustomerGroups,
  getDefaultCustomer,
} from 'actions/customerSearch';
import { getCompanyInfo } from 'actions/Login';
import { getCurrency } from 'actions/configs';
import { fetchProductGroups } from 'actions/productGroupsDB';
import { fetchVatRates } from 'actions/vatRatesDB';
import { fetchProductUnits } from 'actions/productUnitsDB';
import { getHomeStores, getWarehouses } from 'actions/warehouses';
import { fetchEssentialReasonCodes } from 'actions/reasonCodesDB';
import { fetchGiftcardTypes, fetchRegularGiftCards } from 'actions/giftcards';
import { getStoreRegionAssociatedWithSelectedWarehouse } from 'actions/storeRegions';
import { fetchAllRelatedCafaConfigs } from 'actions/integrations/CafaConfigs';
import { ThunkAction } from 'reducers';
import { clearOutdatedReceiptTemplateCache } from 'actions/integrations/printer/receiptPrintoutApi/actions';
import { pluginSetupOnMount } from 'actions/Plugins';
import { getPaymentTypesAction } from 'actions/PaymentTypes';
import { integrationFunctions as getIntegrationFunctions } from 'actions/integrations/terminal';
import { getAssortmentProducts } from 'actions/assortmentProducts';
import { getUserLoggedIn } from 'reducers/Login';
import { getSelectedPos } from 'reducers/PointsOfSale';

import { CAFA_LEVELS } from '../../constants/CAFA';

import { invalidateEssentialData } from './utils';

export function invalidateLevelsUnrelatedToLoggedInLocationAndUser(): ThunkAction {
  return async (dispatch, getState) => {
    const user = getUserLoggedIn(getState());
    const pos = getSelectedPos(getState());
    await invalidateEssentialData(
      ({ level: { level, id } }) =>
        (level === CAFA_LEVELS.User && id !== String(user?.userID)) ||
        (level === CAFA_LEVELS.Pos && id !== String(pos.pointOfSaleID)) ||
        (level === CAFA_LEVELS.Warehouse && id !== String(pos.warehouseID)),
    );
  };
}

export function fetchEssentialData(): ThunkAction {
  return async dispatch => {
    const serviceEndpointsPromise = dispatch(fetchServiceEndpoints());

    await Promise.all(
      [
        getDefaultCustomer(),
        getCompanyInfo(),
        getCurrency(),
        fetchAddressTypes(),
        fetchPersonTitles(),
        fetchJobTitles(),
        getCustomerGroups(),
        fetchProductGroups(),
        fetchVatRates(),
        fetchProductUnits(),
        getHomeStores(),
        fetchRegularGiftCards(),
        fetchGiftcardTypes(),
        fetchEssentialReasonCodes(),
      ].map(dispatch),
    );

    await serviceEndpointsPromise;

    const warehouseDependentData = dispatch(getWarehouses()).then(() =>
      Promise.all([
        dispatch(getStoreRegionAssociatedWithSelectedWarehouse()),
        dispatch(getAssortmentProducts()),
      ]),
    );

    // Cafa fetch configs related to this level + selected integrations config
    // Fetching cafa requires service endpoints to be loaded first
    const cafaDependentData = dispatch(fetchAllRelatedCafaConfigs()).then(() =>
      Promise.all([
        // Cafa loaded, check for template updates and notify MS
        dispatch(clearOutdatedReceiptTemplateCache()),
        // initial computations for enabled plugins
        dispatch(pluginSetupOnMount()),
        // Initial Setup for payment integration
        dispatch(getPaymentTypesAction()).then(() =>
          dispatch(getIntegrationFunctions()).then(data => {
            if (data?.integrationSetup) dispatch(data.integrationSetup());
          }),
        ),
      ]),
    );

    await Promise.all([warehouseDependentData, cafaDependentData]);
  };
}
